import {
  blockLeaves,
  sectionHeadingLeaf,
} from '../convert-grid-report-to-flow';
import type {
  ReportFlowBlock,
  ReportFlowDocument,
  ReportFlowLeafBlock,
} from '../report-flow-types';

export const REPORT_SLIDE = {
  widthIn: 13.333,
  heightIn: 7.5,
  marginXIn: 0.5,
  titleYIn: 0.35,
  titleHIn: 0.6,
  contentYIn: 1.15,
  contentHIn: 5.6,
  footerYIn: 7.0,
  gapIn: 0.3,
} as const;

const REPORT_SLIDE_CONTENT_W_IN =
  REPORT_SLIDE.widthIn - 2 * REPORT_SLIDE.marginXIn;

export const REPORT_SLIDE_TEXT = {
  bodyPt: 12,
  headingPt: [20, 16, 14, 13, 12, 12],
  lineHeight: 1.3,
  paragraphGapPt: 6,
} as const;

export const REPORT_SLIDE_TABLE = { rowHIn: 0.3, gapIn: 0.15 } as const;

const FLOW_CONTENT_WIDTH_PX = 960;
const DEFAULT_CHART_HEIGHT_PX = 420;
const MIN_CHART_H_IN = 2.4;
const MIN_SHRINK_RATIO = 0.6;
const MIN_COLUMN_W_IN = 3.6;
const MIN_STANDALONE_CHART_W_IN = 7;
const PAIRED_TEXT_SHARE = 0.4;
const MIN_TALLEST_ASPECT = 1.2;
const FIXED_HEIGHT_CHART_TYPES: ReadonlySet<string> = new Set(['gauge-v2']);
const MIN_TEXT_SLOT_H_IN = 0.8;
const AVG_CHAR_WIDTH_EM = 0.52;

type SlideBox = { x: number; y: number; w: number; h: number };

export type SlideChartElement = {
  kind: 'chart';
  itemId: string;
  leaf: ReportFlowLeafBlock;
  box: SlideBox;
};

type SlideTextElement = {
  kind: 'text';
  itemId: string;
  markdown: string;
  box: SlideBox;
};

type SlideElement = SlideChartElement | SlideTextElement;

export type SlidePlan = {
  title?: string;
  continued: boolean;
  elements: SlideElement[];
};

type TextCell = { kind: 'text'; itemId: string; blocks: string[] };
type ChartCell = { kind: 'chart'; leaf: ReportFlowLeafBlock };
type Cell = TextCell | ChartCell;

type PlannedRow =
  | { kind: 'text'; breakBefore: boolean; cell: TextCell }
  | { kind: 'columns'; breakBefore: boolean; cells: Cell[]; shares: number[] };

const HEADING_LINE = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const TABLE_DELIMITER_LINE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const splitMarkdownBlocks = (markdown: string): string[] =>
  markdown
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);

const blockFontPt = (block: string): number => {
  const match = HEADING_LINE.exec(block.split('\n')[0] ?? '');
  if (!match) return REPORT_SLIDE_TEXT.bodyPt;
  const level = match[1]?.length ?? 1;
  return REPORT_SLIDE_TEXT.headingPt[level - 1] ?? REPORT_SLIDE_TEXT.bodyPt;
};

const charsPerLineAt = (fontPt: number, widthIn: number) =>
  Math.max(8, Math.floor((widthIn * 72) / (fontPt * AVG_CHAR_WIDTH_EM)));

const lineHeightIn = (line: string, fontPt: number, widthIn: number) =>
  (Math.max(1, Math.ceil(line.length / charsPerLineAt(fontPt, widthIn))) *
    fontPt *
    REPORT_SLIDE_TEXT.lineHeight) /
  72;

const isTableBlock = (lines: string[]) =>
  lines.length >= 2 &&
  (lines[0]?.includes('|') ?? false) &&
  TABLE_DELIMITER_LINE.test(lines[1] ?? '');

/** Matches the deck renderer: one row per table line minus the delimiter row. */
const tableHeightIn = (rowCount: number) =>
  rowCount * REPORT_SLIDE_TABLE.rowHIn + REPORT_SLIDE_TABLE.gapIn;

const estimateBlockHeightIn = (block: string, widthIn: number) => {
  const lines = block.split('\n');
  if (isTableBlock(lines)) return tableHeightIn(lines.length - 1);
  const headingPt = blockFontPt(block);
  const textIn = lines.reduce(
    (sum, line, index) =>
      sum +
      lineHeightIn(
        line,
        index === 0 ? headingPt : REPORT_SLIDE_TEXT.bodyPt,
        widthIn
      ),
    0
  );
  return textIn + REPORT_SLIDE_TEXT.paragraphGapPt / 72;
};

const splitLongLine = (
  line: string,
  fontPt: number,
  widthIn: number,
  maxHeightIn: number
): string[] => {
  const maxLines = Math.max(
    1,
    Math.floor((maxHeightIn * 72) / (fontPt * REPORT_SLIDE_TEXT.lineHeight))
  );
  const budget = charsPerLineAt(fontPt, widthIn) * maxLines;
  const chunks: string[] = [];
  let current = '';
  for (const word of line.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && candidate.length > budget) {
      chunks.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) chunks.push(current);
  return chunks;
};

/** A block taller than a slide is cut at line (or word) boundaries; tables carry their header. */
const splitOversizedBlock = (
  block: string,
  widthIn: number,
  maxHeightIn: number
): string[] => {
  if (estimateBlockHeightIn(block, widthIn) <= maxHeightIn) return [block];
  const lines = block.split('\n');
  if (lines.length === 1) {
    return splitLongLine(block, blockFontPt(block), widthIn, maxHeightIn);
  }
  const [firstLine = ''] = lines;
  if (HEADING_LINE.test(firstLine)) {
    const rest = lines.slice(1).join('\n').trim();
    const headingH = estimateBlockHeightIn(firstLine, widthIn);
    const [lead = '', ...others] = splitOversizedBlock(
      rest,
      widthIn,
      maxHeightIn
    );
    const [leadFit = '', ...leadRest] = splitOversizedBlock(
      lead,
      widthIn,
      Math.max(MIN_TEXT_SLOT_H_IN, maxHeightIn - headingH)
    );
    return [`${firstLine}\n\n${leadFit}`.trim(), ...leadRest, ...others].filter(
      (text) => text.length > 0
    );
  }
  const header = isTableBlock(lines) ? lines.slice(0, 2) : [];
  const chunks: string[][] = [];
  let current: string[] = [];
  for (const line of lines.slice(header.length)) {
    const candidate = [...header, ...current, line].join('\n');
    if (
      current.length > 0 &&
      estimateBlockHeightIn(candidate, widthIn) > maxHeightIn
    ) {
      chunks.push(current);
      current = [line];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) chunks.push(current);
  return chunks.flatMap((chunk) => {
    const text = [...header, ...chunk].join('\n').trim();
    if (!text) return [];
    return chunk.length === 1 && header.length === 0
      ? splitOversizedBlock(text, widthIn, maxHeightIn)
      : [text];
  });
};

const isHeadingOnly = (block: string) =>
  !block.includes('\n') && HEADING_LINE.test(block);

const keepHeadingsWithNext = (blocks: string[]) =>
  blocks.reduce<string[]>((merged, block) => {
    const previous = merged[merged.length - 1];
    if (previous !== undefined && isHeadingOnly(previous)) {
      merged[merged.length - 1] = `${previous}\n\n${block}`;
    } else {
      merged.push(block);
    }
    return merged;
  }, []);

const estimateBlocksHeightIn = (blocks: string[], widthIn: number) =>
  blocks.reduce((sum, block) => sum + estimateBlockHeightIn(block, widthIn), 0);

const isTextLeaf = (leaf: ReportFlowLeafBlock) =>
  leaf.kind === 'text' || leaf.item.plot?.chartType === 'ai-summary';

const leafAspect = (leaf: ReportFlowLeafBlock) => {
  const widthPx = FLOW_CONTENT_WIDTH_PX * ((leaf.widthPct ?? 100) / 100);
  return widthPx / (leaf.heightPx ?? DEFAULT_CHART_HEIGHT_PX);
};

const clampChartHeight = (heightIn: number) =>
  Math.min(REPORT_SLIDE.contentHIn, Math.max(MIN_CHART_H_IN, heightIn));

const shareWidthsIn = (shares: number[]) => {
  const totalGap = REPORT_SLIDE.gapIn * (shares.length - 1);
  const totalShare = shares.reduce((sum, share) => sum + share, 0);
  return shares.map(
    (share) => ((REPORT_SLIDE_CONTENT_W_IN - totalGap) * share) / totalShare
  );
};

const titleFromLeadingHeading = (
  markdown: string
): { title: string; rest: string } | null => {
  const [first, ...rest] = splitMarkdownBlocks(markdown);
  if (!first) return null;
  const [firstLine, ...firstRest] = first.split('\n');
  const match = HEADING_LINE.exec(firstLine ?? '');
  if (!match || (match[1]?.length ?? 3) > 2) return null;
  const title = match[2]?.trim() ?? '';
  if (!title) return null;
  const remainder = [firstRest.join('\n').trim(), ...rest].filter(
    (block) => block.length > 0
  );
  return { title, rest: remainder.join('\n\n') };
};

const cellHeightIn = (cell: Cell, widthIn: number) =>
  cell.kind === 'text'
    ? estimateBlocksHeightIn(cell.blocks, widthIn)
    : clampChartHeight(widthIn / leafAspect(cell.leaf));

const toRows = (
  blocks: ReportFlowBlock[],
  markdownOf: (leaf: ReportFlowLeafBlock) => string
): PlannedRow[] => {
  const toCell = (leaf: ReportFlowLeafBlock): Cell | null => {
    if (!isTextLeaf(leaf)) return { kind: 'chart', leaf };
    const textBlocks = keepHeadingsWithNext(
      splitMarkdownBlocks(markdownOf(leaf))
    );
    return textBlocks.length > 0
      ? { kind: 'text', itemId: leaf.item.id, blocks: textBlocks }
      : null;
  };
  const singleRow = (cell: Cell, breakBefore: boolean): PlannedRow =>
    cell.kind === 'text'
      ? { kind: 'text', breakBefore, cell }
      : { kind: 'columns', breakBefore, cells: [cell], shares: [1] };

  const rows: PlannedRow[] = [];
  for (const block of blocks) {
    let breakBefore = block.startsPage === true;
    const cells = blockLeaves(block).flatMap((leaf) => {
      const cell = toCell(leaf);
      return cell
        ? [{ cell, share: Math.max(leaf.item.position.width, 1) }]
        : [];
    });
    const shares = cells.map(({ share }) => share);
    const widths = shareWidthsIn(shares);
    const keepsColumns =
      cells.length > 1 &&
      widths.every((width) => width >= MIN_COLUMN_W_IN) &&
      cells.every(
        ({ cell }, index) =>
          cellHeightIn(cell, widths[index] ?? 0) <= REPORT_SLIDE.contentHIn
      );
    if (keepsColumns) {
      rows.push({
        kind: 'columns',
        breakBefore,
        cells: cells.map(({ cell }) => cell),
        shares,
      });
      continue;
    }
    for (const { cell } of cells) {
      rows.push(singleRow(cell, breakBefore));
      breakBefore = false;
    }
  }

  const paired: PlannedRow[] = [];
  const [pairedTextW = 0] = shareWidthsIn([
    PAIRED_TEXT_SHARE,
    1 - PAIRED_TEXT_SHARE,
  ]);
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (!row) continue;
    const next = rows[index + 1];
    const [nextCell] = next?.kind === 'columns' ? next.cells : [];
    if (
      row.kind === 'text' &&
      next?.kind === 'columns' &&
      next.cells.length === 1 &&
      !next.breakBefore &&
      nextCell?.kind === 'chart' &&
      estimateBlocksHeightIn(row.cell.blocks, pairedTextW) <=
        REPORT_SLIDE.contentHIn
    ) {
      paired.push({
        kind: 'columns',
        breakBefore: row.breakBefore,
        cells: [row.cell, nextCell],
        shares: [PAIRED_TEXT_SHARE, 1 - PAIRED_TEXT_SHARE],
      });
      index += 1;
      continue;
    }
    paired.push(row);
  }
  return paired;
};

type SlideBuilder = {
  slides: SlidePlan[];
  current: SlidePlan;
  cursorIn: number;
};

const contentBottomIn = REPORT_SLIDE.contentYIn + REPORT_SLIDE.contentHIn;

const isEmpty = (builder: SlideBuilder) =>
  builder.current.elements.length === 0;

const rowTopIn = (builder: SlideBuilder) =>
  builder.cursorIn + (isEmpty(builder) ? 0 : REPORT_SLIDE.gapIn);

const remainingIn = (builder: SlideBuilder) =>
  contentBottomIn - rowTopIn(builder);

const startSlide = (
  builder: SlideBuilder,
  title: string | undefined,
  continued: boolean
) => {
  if (!isEmpty(builder)) builder.slides.push(builder.current);
  builder.current = { title, continued, elements: [] };
  builder.cursorIn = REPORT_SLIDE.contentYIn;
};

const continueSlide = (builder: SlideBuilder) =>
  startSlide(
    builder,
    builder.current.title,
    builder.current.title !== undefined
  );

const placeText = (builder: SlideBuilder, cell: TextCell) => {
  const widthIn = REPORT_SLIDE_CONTENT_W_IN;
  let pending = cell.blocks.flatMap((block) =>
    splitOversizedBlock(block, widthIn, REPORT_SLIDE.contentHIn)
  );
  while (pending.length > 0) {
    if (!isEmpty(builder) && remainingIn(builder) < MIN_TEXT_SLOT_H_IN) {
      continueSlide(builder);
    }
    const available = remainingIn(builder);
    const taken: string[] = [];
    let used = 0;
    for (const block of pending) {
      const height = estimateBlockHeightIn(block, widthIn);
      const fits = used + height <= available;
      if (!fits && (taken.length > 0 || !isEmpty(builder))) break;
      taken.push(block);
      used += height;
    }
    if (taken.length === 0) {
      continueSlide(builder);
      continue;
    }
    const y = rowTopIn(builder);
    const h = Math.min(used, contentBottomIn - y);
    builder.current.elements.push({
      kind: 'text',
      itemId: cell.itemId,
      markdown: taken.join('\n\n'),
      box: { x: REPORT_SLIDE.marginXIn, y, w: widthIn, h },
    });
    builder.cursorIn = y + h;
    pending = pending.slice(taken.length);
  }
};

const columnWidthsFor = (cells: Cell[], shares: number[]) => {
  const [only] = cells;
  if (cells.length !== 1 || only?.kind !== 'chart') {
    return shareWidthsIn(shares);
  }
  const preferred =
    (REPORT_SLIDE_CONTENT_W_IN * (only.leaf.widthPct ?? 100)) / 100;
  return [
    Math.min(
      REPORT_SLIDE_CONTENT_W_IN,
      Math.max(MIN_STANDALONE_CHART_W_IN, preferred)
    ),
  ];
};

const placeColumns = (
  builder: SlideBuilder,
  cells: Cell[],
  shares: number[]
) => {
  const widths = columnWidthsFor(cells, shares);
  const preferredH = clampChartHeight(
    Math.max(
      ...cells.map((cell, index) => cellHeightIn(cell, widths[index] ?? 0))
    )
  );
  const textH = Math.max(
    0,
    ...cells.map((cell, index) =>
      cell.kind === 'text' ? cellHeightIn(cell, widths[index] ?? 0) : 0
    )
  );

  let h = preferredH;
  if (!isEmpty(builder) && h > remainingIn(builder)) {
    const available = remainingIn(builder);
    const canShrink =
      available >=
      Math.max(MIN_CHART_H_IN, textH, preferredH * MIN_SHRINK_RATIO);
    if (canShrink) h = available;
    else continueSlide(builder);
  }

  const y = rowTopIn(builder);
  const [only] = cells;
  const free = REPORT_SLIDE_CONTENT_W_IN - (widths[0] ?? 0);
  const offsetByAlign = { left: 0, center: free / 2, right: free };
  let x =
    REPORT_SLIDE.marginXIn +
    (cells.length === 1 && only?.kind === 'chart'
      ? offsetByAlign[only.leaf.align ?? 'left']
      : 0);

  cells.forEach((cell, index) => {
    const w = widths[index] ?? 0;
    const box = { x, y, w, h };
    builder.current.elements.push(
      cell.kind === 'chart'
        ? { kind: 'chart', itemId: cell.leaf.item.id, leaf: cell.leaf, box }
        : {
            kind: 'text',
            itemId: cell.itemId,
            markdown: cell.blocks.join('\n\n'),
            box,
          }
    );
    x += w + REPORT_SLIDE.gapIn;
  });
  builder.cursorIn = y + h;
};

const growChartsIntoSpareSpace = (slide: SlidePlan): SlidePlan => {
  const charts = slide.elements.filter(
    (element): element is SlideChartElement => element.kind === 'chart'
  );
  if (charts.length === 0) return slide;
  const bottom = Math.max(
    ...slide.elements.map((element) => element.box.y + element.box.h)
  );
  const spare = contentBottomIn - bottom;
  if (spare <= 0.05) return slide;

  const chartRowTops = Array.from(
    new Set(charts.map((element) => element.box.y))
  );
  const growthByRow = new Map(
    chartRowTops.map((top) => {
      const headroom = Math.min(
        ...charts
          .filter((element) => element.box.y === top)
          .map((element) =>
            FIXED_HEIGHT_CHART_TYPES.has(
              element.leaf.item.plot?.chartType ?? ''
            )
              ? 0
              : Math.min(
                  REPORT_SLIDE.contentHIn,
                  element.box.w / MIN_TALLEST_ASPECT
                ) - element.box.h
          )
      );
      return [
        top,
        Math.max(0, Math.min(spare / chartRowTops.length, headroom)),
      ];
    })
  );

  return {
    ...slide,
    elements: slide.elements.map((element) => {
      const shift = chartRowTops
        .filter((top) => top < element.box.y)
        .reduce((sum, top) => sum + (growthByRow.get(top) ?? 0), 0);
      const grow = growthByRow.get(element.box.y) ?? 0;
      return {
        ...element,
        box: {
          ...element.box,
          y: element.box.y + shift,
          h: element.box.h + grow,
        },
      };
    }),
  };
};

export const planReportSlides = (
  doc: ReportFlowDocument,
  textByItemId: ReadonlyMap<string, string>
): SlidePlan[] => {
  const builder: SlideBuilder = {
    slides: [],
    current: { continued: false, elements: [] },
    cursorIn: REPORT_SLIDE.contentYIn,
  };

  for (const section of doc.sections) {
    const overrides = new Map<string, string>();
    const markdownOf = (leaf: ReportFlowLeafBlock) =>
      overrides.get(leaf.item.id) ??
      textByItemId.get(leaf.item.id)?.trim() ??
      '';

    let title = section.title;
    const [firstBlock] = section.blocks;
    const headingLeaf =
      firstBlock && title !== undefined
        ? sectionHeadingLeaf(firstBlock)
        : undefined;
    const leading = headingLeaf
      ? titleFromLeadingHeading(markdownOf(headingLeaf))
      : null;
    if (headingLeaf && leading) {
      title = leading.title;
      overrides.set(headingLeaf.item.id, leading.rest);
    }
    startSlide(builder, title ?? doc.title, false);

    for (const row of toRows(section.blocks, markdownOf)) {
      if (row.breakBefore && !isEmpty(builder)) continueSlide(builder);
      if (row.kind === 'text') placeText(builder, row.cell);
      else placeColumns(builder, row.cells, row.shares);
    }
  }
  if (!isEmpty(builder)) builder.slides.push(builder.current);

  return builder.slides.map(growChartsIntoSpareSpace);
};
